import {groupByFn} from "../reducers";
import {getPricingEntry} from "../../redux/products/productsDataExtractors";
import {type NetPriceData, type PriceData} from "../../models/PriceData";
import {type LineItemBuilder} from "./LineItemUpdate";
import {getNetPrice, getPriceInfo} from "../../feature/ProductDescriptor/v1/productDescriptorUtils";
import {INVALID_PACKAGE_CODE} from "../constants";
import {calculateSalesCampaign4PDP} from "~/SalesCampaign/utils";
import {lower} from "~/utils/PriceUtils";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

/**
 * Refresh data coming from PriceInfo
 * @param allLineItems - all line items
 * @param descriptorsByPriceReference - product descriptors map by price ref
 */
export function refreshPriceInfoData(
  allLineItems: LineItemBuilder[],
  descriptorsByPriceReference: Dict<ProductDescriptor>,
) {
  const byPriceReference = groupByFn(allLineItems, (item) => item.get("priceReference"));

  for (const [priceReference, lineItemsForReference] of Object.entries(byPriceReference)) {
    const productDescriptor = descriptorsByPriceReference[priceReference];
    if (productDescriptor) {
      refreshPricesForProductAndPriceReference(lineItemsForReference, productDescriptor);
    }
  }
}

function refreshPricesForProductAndPriceReference(
  allLineItems: LineItemBuilder[],
  productDescriptor: ProductDescriptor,
) {
  if (productDescriptor.prices.length === 0) {
    return;
  }

  if (productDescriptor.isMultiPrice) {
    // 15 digit prices case
    const byPackage = groupByFn(allLineItems, (item) => item.get("packCode"));
    for (const [packCode, lineItems] of Object.entries(byPackage)) {
      refreshCoO(lineItems, productDescriptor, packCode);
      const lineItemsWithUpdatablePrice = lineItems.filter(
        (item) => !item.get("quoteNumber") && !item.get("contractNumber"),
      );
      refreshPrices(lineItemsWithUpdatablePrice, productDescriptor, packCode);
    }
  } else {
    // 13 digit prices case
    refreshCoO(allLineItems, productDescriptor);
    const lineItemsWithUpdatablePrice = allLineItems.filter(
      (item) => !item.get("quoteNumber") && !item.get("contractNumber"),
    );
    refreshPrices(lineItemsWithUpdatablePrice, productDescriptor);
  }
}

/*
 *  The line items passed here are line items that can have their price updated
 */
function refreshPrices(lineItems: LineItemBuilder[], productDescriptor: ProductDescriptor, packCode?: string) {
  const totalQuantity = lineItems.reduce((sum, item) => sum + item.get("quantity"), 0);
  const selectPriceProps = {
    product: productDescriptor,
    packCode: packCode ?? INVALID_PACKAGE_CODE,
    totalQuantity,
  };
  const netPrice = getNetPrice(selectPriceProps);
  const salePerPack = calculateSalesCampaign4PDP({ productDescriptor })?.data?.salePerPack;
  if (!netPrice) {
    return;
  }
  lineItems.forEach((lineItem) => {
    if (lineItem.get("saleCampaignEnabled")) {
      const salesData = salePerPack?.[lineItem.get("packCode")];
      const actualSalePrice = salesData?.actualPrice;

      if (actualSalePrice) {
        if (!isSame(lineItem.get("actualSalePrice"), actualSalePrice)) {
          lineItem.set("actualSalePrice", actualSalePrice);
        }
        let lowerPrice = lower(netPrice, actualSalePrice);
        if (lowerPrice && netPrice) {
          lowerPrice = {
            ...netPrice,
            ...lowerPrice,
            // @ts-ignore
            baseValue: lowerPrice?.value,
          };
          if (!isSame(lineItem.get("netPrice"), lowerPrice)) {
            lineItem.set("netPrice", lowerPrice as NetPriceData);
          }
        }
      } else {
        // !salesPrice
        lineItem.set("saleCampaignEnabled", false);
        lineItem.set("saleCampaignCode", "");
        lineItem.set("actualSalePrice", undefined);
      }
    } else {
      if (!isSame(lineItem.get("netPrice"), netPrice)) {
        lineItem.set("netPrice", netPrice);
      }
    }
    if (!isSame(lineItem.get("actualNetPrice"), netPrice)) {
      lineItem.set("actualNetPrice", netPrice);
    }
  });

  const priceInfo = getPriceInfo({ product: productDescriptor, packCode: packCode ?? INVALID_PACKAGE_CODE });
  if (!priceInfo) {
    return;
  }
  const pricingEntry = getPricingEntry(priceInfo, totalQuantity);
  if (!pricingEntry) {
    return;
  }

  lineItems.forEach((item) => {
    if (!isSame(item.get("msp"), pricingEntry.msp)) {
      item.set("msp", pricingEntry.msp);
    }
    if (!isSame(item.get("rsp"), pricingEntry.rsp)) {
      item.set("rsp", pricingEntry.rsp);
    }
  });
}

function refreshCoO(lineItems: LineItemBuilder[], productDescriptor: ProductDescriptor, packCode?: string) {
  const priceInfo = getPriceInfo({ product: productDescriptor, packCode: packCode ?? INVALID_PACKAGE_CODE });
  if (!priceInfo) {
    return;
  }
  lineItems.forEach((item) => {
    if (item.get("countryOfOrigin") !== (priceInfo.originCountry ?? "")) {
      item.set("countryOfOrigin", priceInfo.originCountry ?? "");
    }
  });
}

function isSame(a: PriceData | undefined, b: PriceData | undefined): boolean;
function isSame(a: NetPriceData | undefined, b: NetPriceData | undefined): boolean;
function isSame(a: NetPriceData | PriceData | undefined, b: NetPriceData | PriceData | undefined) {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  let same = a.currencyIso === b.currencyIso && a.value === b.value;
  if (!same) {
    return false;
  }
  if ("surcharge" in a && "surcharge" in b) {
    same =
      a.surcharge === b.surcharge &&
      a.unit === b.unit &&
      a.isSpecial === b.isSpecial &&
      a.rebate === b.rebate &&
      a.isSubscription === b.isSubscription;
  }
  return same;
}
